import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {NoSuchCell, NoSuchColumn} from '@mintdata/mint-kit/api/values/frames/FrameErrors';
import {FrameMeta} from '@mintdata/mint-kit/api/values/frames/FrameMeta';
import {IFrameRowsVisitor} from '@mintdata/mint-kit/api/values/frames/FrameVisitor';
import {FieldRef} from '@mintdata/mint-kit/impl/frames/FieldRef';
import {serializable} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';

export type FrameMetaKey = string;
export type FrameMetaValue = string;
export type FieldName = string;
export type RowIndex = number;
export type ColumnOffset = number;
export type FrameData = Array<Array<IValue>>;

export type IKeyedMap = Map<string, IValue>;

export type SortComparator = (a:any, b:any) => number;

export interface IFrameField
{
    readonly name:FieldName;
    readonly type:Type;

    clone():IFrameField;
}

export interface IFrameRecord
{
    getFieldNames():Array<FieldName>;

    getCells():Array<IValue>;

    hasCell(fieldName:FieldName, isIgnoreCase?:boolean):boolean;

    getCell(fieldName:FieldName, isIgnoreCase?:boolean):IValue;

    getCellAtOffset(columnOffset:ColumnOffset):IValue;

    // hasErrors:boolean;
}

export interface IFrameColumn<T extends IValue>
{
    fieldName:FieldName;
    fieldType:Type;
    rows:T[];
}

export interface IFrameCoordinate
{
    fieldName:FieldName;
    rowIndex:RowIndex;
}

export interface IFrameCell<T extends IValue>
{
    fieldName:FieldName;
    rowIndex:RowIndex;
    value:T;
}

export class FrameCellCoordinate implements IFrameCoordinate
{
    @serializable public readonly fieldName:FieldName;
    @serializable public readonly rowIndex:RowIndex;

    public constructor(fieldName:FieldName, rowIndex:RowIndex)
    {
        this.fieldName = fieldName;
        this.rowIndex = rowIndex;
    }

    public isEqualTo(frameCellCoordinate:FrameCellCoordinate):boolean
    {
        return frameCellCoordinate
            && this.fieldName === frameCellCoordinate.fieldName
            && this.rowIndex === frameCellCoordinate.rowIndex;
    }

    public withFieldName(newFieldName:FieldName):FrameCellCoordinate
    {
        return new FrameCellCoordinate(newFieldName, this.rowIndex);
    }

    public withRowIndex(newRowIndex:RowIndex):FrameCellCoordinate
    {
        return new FrameCellCoordinate(this.fieldName, newRowIndex);
    }
}

export type FramePath = Array<FrameCellCoordinate>;

export interface IFrame extends IValue
{
    readonly isEmpty:boolean;
    readonly hasErrors:boolean;
    readonly recordCount:number;
    readonly fieldCount:number;

    getRecords():Array<IFrameRecord>;

    getFields():Array<IFrameField>;

    getFieldsUnsafe():Array<IFrameField>;

    getFieldNames():Array<FieldName>;

    getFieldNamesByPath(pathInFrame:FramePath):Array<FieldName>;

    findField(fieldName:FieldName):IFrameField;

    getColumn<T extends IValue>(fieldName:FieldName):IFrameColumn<T> | NoSuchColumn;

    getColumnAsFrame<T extends IValue>(fieldName:FieldName):IFrame | NoSuchColumn;

    getCell(fieldName:FieldName, rowIndex:RowIndex):IValue | NoSuchCell;

    getCellByPath(pathInFrame:FramePath):IValue | NoSuchCell;

    getCellByOffset(columnOffset:ColumnOffset, rowIndex:RowIndex):IValue | NoSuchCell;

    getMeta():FrameMeta;

    getData():FrameData;

    getDataUnsafe():FrameData;

    getAllValues():IValue[];

    getRowsCount():number;

    withCellByPath(pathInFrame:FramePath, value:IValue):IFrame;

    withFieldRenamed<T extends IValue>(currentFieldName:FieldName, newFieldName:FieldName):IFrame;

    withColumns<T extends IValue>(columns:Array<IFrameColumn<T>>, index?:number):IFrame;

    withoutColumns(indices:Array<number>):IFrame;

    withColumnSubset<T extends IValue>(fieldRefs:Array<FieldRef>, isIgnoreCase?:boolean):IFrame

    withColumnsReordered(oldIndex:ColumnOffset, newIndex:ColumnOffset, length:number):IFrame;

    withRecords(records:FrameData, index?:number):IFrame;

    withoutRecords(indices:Array<number>):IFrame;

    withRecordsSubset(rowIndicies:Array<RowIndex>):IFrame;

    withRecordsReordered(oldIndex:RowIndex, newIndex:RowIndex, length:number):IFrame;

    withRecordsSorted(fieldName:FieldName, comparator:SortComparator, sortedIndexMap?:Array<number>):IFrame;

    withCells<T extends IValue>(cells:Array<IFrameCell<T>>):IFrame;

    withoutCells<T extends IValue>(cells:Array<FrameCellCoordinate>):IFrame;

    withMeta(meta:FrameMeta):IFrame;

    append(frame:IFrame, resultRecordsCountLimit?:number):IFrame;

    join(frame2:IFrame, joinFieldName1:FieldName, joinFieldName2?:FieldName, frame1Alias?:string, frame2Alias?:string):IFrame | AError;

    extendWith(frame:IFrame):IFrame;

    toJSON():Object;

    acceptAsync(visitor:IFrameRowsVisitor):Promise<any>;
}
